import { useCallback, useEffect, useRef, useState } from 'react';

import { useWebSocket } from 'src/hooks/useWebSocket';
import {
  AudienceDefinition,
  AudiencePreview,
  Channel,
  RecipientRule,
} from 'src/pages/Campaigns/types/Campaigns';
import { CampaignsService } from 'src/pages/Campaigns/services/Campaigns.service';

const DEBOUNCE_MS = 400;

export interface UseAudiencePreviewArgs {
  propertyId?: number | null;
  audience: AudienceDefinition;
  recipientRule: RecipientRule;
  respectPatGates: boolean;
  channels: Channel[];
  enabled?: boolean;
}

export interface UseAudiencePreviewResult {
  preview: AudiencePreview | null;
  loading: boolean;
  error: string | null;
  /**
   * The request signature the current numbers were produced for. A count card
   * must never render numbers whose signature differs from the form on screen
   * (CAMP-002: a stale preview sat next to a freshly toggled gate switch and read
   * as if the gate were being ignored).
   */
  signature: string;
  /** True while the numbers on screen describe an older form state. */
  stale: boolean;
}

/**
 * Debounced count-only preview. One request 400 ms after the last change; an
 * in-flight request is superseded by the next one (the sequence number guard), so
 * a fast toggle run cannot leave a stale count on screen.
 */
export function useAudiencePreview({
  propertyId,
  audience,
  recipientRule,
  respectPatGates,
  channels,
  enabled = true,
}: UseAudiencePreviewArgs): UseAudiencePreviewResult {
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [loadedSignature, setLoadedSignature] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sequenceRef = useRef(0);

  const audienceKey = JSON.stringify(audience);
  const channelKey = channels.join(',');
  const signature = JSON.stringify({
    audience: audienceKey,
    recipientRule,
    respectPatGates,
    channels: channelKey,
  });

  useEffect(() => {
    if (!enabled || !propertyId) {
      return undefined;
    }
    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    const requested = signature;

    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      CampaignsService.preview({
        property: propertyId,
        audience: JSON.parse(audienceKey) as AudienceDefinition,
        recipient_rule: recipientRule,
        respect_pat_gates: respectPatGates,
        channels: channelKey.split(',').filter(Boolean),
      })
        .then((response) => {
          if (sequenceRef.current === sequence) {
            setPreview(response.data);
            setLoadedSignature(requested);
          }
        })
        .catch(() => {
          if (sequenceRef.current === sequence) {
            setError('Could not preview audience. Try again.');
          }
        })
        .finally(() => {
          if (sequenceRef.current === sequence) {
            setLoading(false);
          }
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    propertyId,
    audienceKey,
    recipientRule,
    respectPatGates,
    channelKey,
    enabled,
    signature,
  ]);

  return {
    preview,
    loading,
    error,
    signature,
    stale: loadedSignature !== null && loadedSignature !== signature,
  };
}

export interface UseCampaignWebSocketArgs {
  campaignId?: number | null;
  enabled?: boolean;
  onLive?: (live: unknown) => void;
  onRecipientsChanged?: (rows: unknown[], stage?: string) => void;
}

export interface UseCampaignWebSocketResult {
  isConnected: boolean;
  connectionError: string | null;
  reconnectStopped: boolean;
}

/**
 * Live run state for one campaign (DD-025). ``genericHandler`` is memoised with an
 * empty dep list and the callbacks live in refs — passing new function identities
 * into ``useWebSocket`` is what causes its connect/disconnect loop. Unhandled
 * event types are ignored here rather than logged per message (the
 * ``messageHandlers`` fallback ``console.error``s, which is the DD-025 violation).
 */
export function useCampaignWebSocket({
  campaignId,
  enabled = true,
  onLive,
  onRecipientsChanged,
}: UseCampaignWebSocketArgs): UseCampaignWebSocketResult {
  const onLiveRef = useRef(onLive);
  const onRecipientsRef = useRef(onRecipientsChanged);
  onLiveRef.current = onLive;
  onRecipientsRef.current = onRecipientsChanged;

  const genericHandler = useCallback((type: string, data: unknown): void => {
    const payload = (data ?? {}) as {
      campaign?: unknown;
      recipients?: unknown[];
      stage?: string;
    };

    if (
      type === 'campaign.recipients_changed' ||
      type === 'campaign.recipients_snapshot'
    ) {
      if (Array.isArray(payload.recipients)) {
        onRecipientsRef.current?.(payload.recipients, payload.stage);
      }
      return;
    }
    if (
      type === 'campaign.progress' ||
      type === 'campaign.snapshot' ||
      type === 'campaign.terminal'
    ) {
      if (payload.campaign) {
        onLiveRef.current?.(payload.campaign);
      }
    }
  }, []);

  const active = Boolean(campaignId) && enabled;
  const { isConnected, connectionError, reconnectStopped } = useWebSocket({
    enabled: active,
    url: `campaigns/${campaignId}/`,
    genericHandler,
    reconnectAttempts: 20,
    reconnectDelay: 3000,
  });

  return { isConnected, connectionError, reconnectStopped };
}

export interface UseCampaignsPropertySocketArgs {
  propertyId?: number | null;
  enabled?: boolean;
  onCampaign?: (campaign: unknown) => void;
}

/** Property list feed: created / updated rows, applied by id. */
export function useCampaignsPropertySocket({
  propertyId,
  enabled = true,
  onCampaign,
}: UseCampaignsPropertySocketArgs): UseCampaignWebSocketResult {
  const onCampaignRef = useRef(onCampaign);
  onCampaignRef.current = onCampaign;

  const genericHandler = useCallback((type: string, data: unknown): void => {
    const payload = (data ?? {}) as { campaign?: unknown };
    if (
      (type === 'campaign.created' || type === 'campaign.updated') &&
      payload.campaign
    ) {
      onCampaignRef.current?.(payload.campaign);
    }
  }, []);

  const active = Boolean(propertyId) && enabled;
  const { isConnected, connectionError, reconnectStopped } = useWebSocket({
    enabled: active,
    url: `campaigns/property/${propertyId}/`,
    genericHandler,
    reconnectAttempts: 20,
    reconnectDelay: 3000,
  });

  return { isConnected, connectionError, reconnectStopped };
}

export interface UseCampaignTraySocketArgs {
  enabled?: boolean;
  onSnapshot?: (rows: unknown[]) => void;
  onCreated?: (row: unknown) => void;
  onUpdated?: (row: unknown) => void;
  onRemoved?: (campaignId: number) => void;
}

/** The floating tray's feed: every non-terminal campaign the user can see. */
export function useCampaignTraySocket({
  enabled = true,
  onSnapshot,
  onCreated,
  onUpdated,
  onRemoved,
}: UseCampaignTraySocketArgs): UseCampaignWebSocketResult {
  const snapshotRef = useRef(onSnapshot);
  const createdRef = useRef(onCreated);
  const updatedRef = useRef(onUpdated);
  const removedRef = useRef(onRemoved);
  snapshotRef.current = onSnapshot;
  createdRef.current = onCreated;
  updatedRef.current = onUpdated;
  removedRef.current = onRemoved;

  const genericHandler = useCallback((type: string, data: unknown): void => {
    const payload = (data ?? {}) as {
      campaigns?: unknown[];
      campaign?: unknown;
      campaign_id?: number;
    };
    if (type === 'tray.snapshot' && Array.isArray(payload.campaigns)) {
      snapshotRef.current?.(payload.campaigns);
      return;
    }
    if (type === 'tray.campaign_created' && payload.campaign) {
      createdRef.current?.(payload.campaign);
      return;
    }
    if (type === 'tray.campaign_updated' && payload.campaign) {
      updatedRef.current?.(payload.campaign);
      return;
    }
    if (
      type === 'tray.campaign_removed' &&
      typeof payload.campaign_id === 'number'
    ) {
      removedRef.current?.(payload.campaign_id);
    }
  }, []);

  const { isConnected, connectionError, reconnectStopped } = useWebSocket({
    enabled,
    url: 'campaigns/tray/',
    genericHandler,
    reconnectAttempts: 20,
    reconnectDelay: 3000,
  });

  return { isConnected, connectionError, reconnectStopped };
}
